/**
 * Proximity tab (`ldt-proximity`, spec §12):
 *   - Op type / approval / buffer header
 *   - Airports list (with show-on-map button)
 *   - Airspaces list (with altitude filter slider + show-on-map)
 *   - Obstacles list (with show-on-map)
 */
import { useEffect, useState } from 'react';
import type { DroneLive } from '@/lib/store/drones';
import { getMap } from '@/lib/map/instance';
import type { AirportNearby, AirspaceNearby, ObstacleNearby } from '@/lib/mqtt/schemas/proximity';
import { Section, SectionGroup } from '@/components/Section';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { MapPin } from 'lucide-react';
import { useUiStore } from '@/lib/store/ui';

const AIRSPACE_COLORS: Record<string, string> = {
  B: '#1d4ed8',
  C: '#c41a1a',
  D: '#7c3aed',
  E: '#059669',
  F: '#d97706',
  G: '#22a050',
  R: '#8b0000',
  A: '#7c3aed',
};

export function ProximityTab({ drone }: { drone: DroneLive }) {
  const t = drone.telemetry;
  const p = drone.proximity;
  const isBVLOS = (p?.airports && (drone.oi?.op_type ?? '').includes('BVLOS')) ?? false;
  const [maxFt, setMaxFt] = useState(3000);
  const showApt = useUiStore((s) => s.showProxAirports);
  const showAs = useUiStore((s) => s.showProxAirspaces);
  const showObs = useUiStore((s) => s.showProxObstacles);
  const toggle = useUiStore((s) => s.toggle);

  // Mockup parity: entering the Proximity tab auto-zooms the map to fit
  // the drone + its Eval ring (~12 km half-side).
  useEffect(() => {
    const m = getMap();
    if (!m) return;
    const r = (drone.telemetry.evalRadius ?? 12_000) * 1.6;
    const dLat = r / 111_320;
    const dLng = r / (111_320 * Math.cos((drone.telemetry.lat * Math.PI) / 180));
    m.fitBounds(
      [
        [drone.telemetry.lng - dLng, drone.telemetry.lat - dLat],
        [drone.telemetry.lng + dLng, drone.telemetry.lat + dLat],
      ],
      { padding: 60, duration: 500 },
    );
  }, [drone.id]);

  return (
    <SectionGroup>
      <header className="flex items-center gap-1.5 flex-wrap">
        <Badge variant={isBVLOS ? 'solid' : 'info'}>{drone.oi?.op_type ?? '—'}</Badge>
        <Badge variant="muted">{drone.oi?.ops_approval ?? 'Standard'}</Badge>
        <span className="text-[0.615em] text-(--color-text3)">
          Airport buffer: {isBVLOS ? '5 nm' : '3 nm'} · Heliport: {isBVLOS ? '5 nm' : '1 nm'}
        </span>
      </header>

      <Section
        title={
          <span className="flex items-center gap-3">
            Air Risks
            <span className="text-(--color-text3) font-mono normal-case">
              <span className="text-[0.692em] text-(--color-text2) font-bold">Aircraft:</span> {t.agl} ft AGL <span className="opacity-60">/</span> {Math.round(t.asl ?? 0).toLocaleString()} ft ASL
            </span>
          </span>
        }
      >
        <SubSectionHeader title="Airports" actionLabel="Show on Map" active={showApt} onClick={() => toggle('showProxAirports')} />
        <ul className="space-y-1 mb-3">
          {(p?.airports ?? []).map((a) => (
            <AirportRow key={a.icao} a={a} />
          ))}
          {(p?.airports.length ?? 0) === 0 && (
            <li className="text-[0.769em] text-(--color-text3)">No airports in range.</li>
          )}
        </ul>

        <SubSectionHeader
          title={
            <span className="flex items-center gap-1.5">
              Airspace
              <span className="text-[0.615em] text-(--color-text3) font-normal">Current:</span>
              <span
                className="text-[0.615em] font-bold px-1.5 py-0.5 rounded text-white"
                style={{ background: AIRSPACE_COLORS[p?.current_airspace ?? 'G'] ?? '#22a050' }}
              >
                Class {p?.current_airspace ?? '—'}
              </span>
            </span>
          }
          actionLabel="Show on Map"
          active={showAs}
          onClick={() => toggle('showProxAirspaces')}
        />
        <div className="flex items-center gap-2 mb-2 rounded-md border border-(--color-border) bg-(--color-surface) px-2 py-1.5">
          <span className="text-[0.615em] text-(--color-text3) font-mono">SFC</span>
          <input
            type="range"
            min={0}
            max={3000}
            step={100}
            value={maxFt}
            onChange={(e) => setMaxFt(Number(e.target.value))}
            className="flex-1 accent-(--color-blue) cursor-pointer"
          />
          <span className="text-[0.615em] font-bold font-mono text-(--color-blue) min-w-[3rem] text-right">{maxFt} ft</span>
        </div>
        <ul className="space-y-1 mb-3">
          {(p?.airspaces ?? []).filter((a) => a.floorFt <= maxFt).map((a) => (
            <AirspaceRow key={a.name} a={a} />
          ))}
          {(p?.airspaces.length ?? 0) === 0 && (
            <li className="text-[0.769em] text-(--color-text3)">No airspaces nearby.</li>
          )}
        </ul>
      </Section>

      <Section title="Ground Risks">
        <SubSectionHeader title="Obstacles" actionLabel="Show on Map" active={showObs} onClick={() => toggle('showProxObstacles')} />
        <ul className="space-y-1">
          {(p?.obstacles ?? []).map((o) => (
            <ObstacleRow key={o.name} o={o} />
          ))}
          {(p?.obstacles.length ?? 0) === 0 && (
            <li className="text-[0.769em] text-(--color-text3)">No obstacles in range.</li>
          )}
        </ul>
      </Section>
    </SectionGroup>
  );
}

function SubSectionHeader({ title, actionLabel, active, onClick }: { title: React.ReactNode; actionLabel: string; active?: boolean; onClick?: () => void }) {
  return (
    <div className="flex items-center justify-between mb-1">
      <h4 className="text-[0.692em] font-bold uppercase tracking-wider text-(--color-text2)">{title}</h4>
      <Button size="xs" variant={active ? 'default' : 'outline'} onClick={onClick}>
        <MapPin size={10} /> {active ? 'Hide' : actionLabel}
      </Button>
    </div>
  );
}

function AirportRow({ a }: { a: AirportNearby }) {
  const tone = a.within ? 'crit' : a.inBuffer ? 'warn' : 'muted';
  return (
    <li
      className={cn(
        'flex items-center justify-between gap-2 rounded-md bg-(--color-surface) px-2.5 py-1.5 border-l-2 border border-(--color-border)',
        tone === 'crit' && 'border-l-(--color-red)',
        tone === 'warn' && 'border-l-(--color-amber)',
      )}
    >
      <div className="flex items-center gap-1.5 min-w-0">
        <span className="text-[0.769em] font-bold">{a.icao}</span>
        <Badge variant={a.type === 'Heliport' ? 'info' : a.type === 'Certified' ? 'solid' : 'muted'}>{a.type}</Badge>
        {a.within && <Badge variant="crit">WITHIN ATZ</Badge>}
        <span className="text-[0.692em] text-(--color-text3) truncate">{a.name}</span>
      </div>
      <span
        className={cn(
          'text-[0.692em] font-mono font-bold shrink-0',
          tone === 'crit' && 'text-(--color-red)',
          tone === 'warn' && 'text-(--color-amber)',
        )}
      >
        {a.within ? 'Within ATZ' : `${a.distNm.toFixed(1)} nm`}
      </span>
    </li>
  );
}

function AirspaceRow({ a }: { a: AirspaceNearby }) {
  const color = AIRSPACE_COLORS[a.class] ?? '#6b7280';
  return (
    <li
      className={cn(
        'flex items-center justify-between gap-2 rounded-md bg-(--color-surface) px-2.5 py-1.5 border-l-2 border border-(--color-border)',
        a.within && 'border-l-(--color-red)',
      )}
      style={a.within ? undefined : { borderLeftColor: color }}
    >
      <div className="flex items-center gap-1.5 min-w-0">
        <span
          className="text-[0.615em] font-bold px-1.5 py-0.5 rounded text-white"
          style={{ background: color }}
        >
          {a.class}
        </span>
        <span className="text-[0.769em] truncate">{a.name}</span>
        <span className="text-[0.615em] font-mono text-(--color-text3)">
          {a.floorFt} – {a.ceilFt} ft
        </span>
      </div>
      <span className="text-[0.692em] font-mono font-bold" style={{ color: a.within ? color : undefined }}>
        {a.within ? 'WITHIN' : `${a.distNm.toFixed(1)} nm`}
      </span>
    </li>
  );
}

function ObstacleRow({ o }: { o: ObstacleNearby }) {
  const close = o.distKm < 1;
  return (
    <li
      className={cn(
        'flex items-center justify-between gap-2 rounded-md bg-(--color-surface) px-2.5 py-1.5 border-l-2 border border-(--color-border)',
        close && 'border-l-(--color-amber)',
      )}
    >
      <div className="flex items-center gap-1.5 min-w-0">
        <span className="text-[0.769em] font-bold">{o.name}</span>
        <span className="text-[0.615em] text-(--color-text3)">{o.type}</span>
        <Badge variant={o.above ? 'info' : 'ok'}>{o.above ? '↑ ABOVE' : '↓ BELOW'}</Badge>
      </div>
      <span className={cn('text-[0.692em] font-mono font-bold', close && 'text-(--color-amber)')}>
        {o.distKm.toFixed(1)} km
      </span>
    </li>
  );
}
